/**
 * "Group them as one study": the one press that answers `onestudy`, from the
 * coach's card and from the node card's line alike.
 *
 * It writes what the Lab's "Human override" form writes — a same-family
 * `GenealogyOverride` per join, each with its reason — through the path that
 * form uses (`EvidencePanel.saveOverride` in `ResearchLab.tsx`): the projected
 * records the overrides name are made durable first, so the codec can validate
 * both ends on reload, then the overrides are upserted and the research record is
 * set. So the grouping lands in the Lab's merge-decision ledger as `human-same`
 * rows with their reasons, a later "Keep separate" there outranks it exactly as
 * it would outrank the form's, and the independence count on the card and in the
 * Evidence tab moves together because they are one count.
 *
 * WHAT IT NEVER MOVES is a reading. The refuted gate (`refutedIsEarned` in
 * `core/types.ts`) reads each SOURCE's own fields — the DOI in its URL, its text
 * signatures, its `quoteFound`, its stance — and never the research record, so no
 * override reaches it, live or on the way off disk. A grouping does move what the
 * Lab already derives from families for any override: the epistemic compiler's
 * independence units and the evidence-rebase join (`core/rebase.ts`). Those are
 * the Lab's own counts, not the claim's reading.
 *
 * No model runs. The press re-reads the claim's group at the moment it is made
 * (`oneStudyOf`), because the cue that offered it is a snapshot and a check may
 * have landed since; what it groups is what the toast then names. Undo takes back
 * exactly the overrides this press wrote and nothing else — a newer decision on
 * the same pair made in the meantime is left alone.
 */

import { oneStudyOf } from '../core/cues'
import { sessionEvidence } from '../core/claimEvidence'
import type { GenealogyOverride, KinEdge } from '../core/genealogy'
import { uid } from '../core/id'
import { createResearchState, upsertResearchEntities } from '../core/research'
import { useStore } from '../core/store'

/** The reason each override records: the resemblance that joined the pair, in the
 *  words the merge ledger prints beside it. */
export function groupingReason(edge: KinEdge): string {
  return edge.signal.rule === 'near-text'
    ? `One study, accepted from a suggestion: page text ${edge.signal.hamming} of 64 bits apart, on two sites`
    : `One study, accepted from a suggestion: the same title on two sites — “${edge.signal.title}”`
}

/**
 * Group the claim's largest resembling set of reports as one study. Returns how
 * many reports it grouped, or 0 when there was nothing left to group — said out
 * loud either way.
 */
export function groupReports(nodeId: string): number {
  const s = useStore.getState()
  const session = s.session
  const reading = oneStudyOf(session, nodeId)
  if (!reading) {
    s.toast(
      'Nothing left to group on this claim — its reports are already counted as they stand.',
      'info',
    )
    return 0
  }
  const at = Date.now()
  const overrides: GenealogyOverride[] = reading.cluster.joins.map((edge) => ({
    id: uid('override'),
    leftId: edge.leftId,
    rightId: edge.rightId,
    decision: 'same',
    reason: groupingReason(edge),
    at,
  }))
  const named = new Set(overrides.flatMap((o) => [o.leftId, o.rightId]))
  const base =
    session.research ??
    createResearchState({ id: `${session.id}:research`, now: session.createdAt })
  // The records first, as the Lab's form does: an override whose ends are only
  // projected would be dropped by the codec on the next read.
  let next = upsertResearchEntities(
    base,
    'evidenceRecords',
    sessionEvidence(session).records.filter((row) => named.has(row.id)),
    at,
  )
  next = upsertResearchEntities(next, 'genealogyOverrides', overrides, at)
  s.setResearchState(next)

  const n = reading.cluster.recordIds.length
  const written = new Set(overrides.map((o) => o.id))
  s.toast(
    `Grouped ${n} reports as one study. The Lab’s merge ledger records it as your decision.`,
    'good',
    {
      label: 'Undo',
      run: () => {
        const cur = useStore.getState()
        const research = cur.session.research
        if (!research) return
        cur.setResearchState({
          ...research,
          genealogyOverrides: research.genealogyOverrides.filter((o) => !written.has(o.id)),
          updatedAt: Math.max(research.updatedAt, Date.now()),
        })
      },
    },
  )
  return n
}

/** Frames to wait for the lazily loaded Lab to draw — see `LAB_FRAMES` in
 *  `disagreePress.ts`, whose door this one mirrors. */
const LAB_FRAMES = 120

/**
 * The count's door: the Lab's Evidence tab, opened on its Families list — the
 * independence units the card's clause counts, each with the rule that joined
 * it, and the merge ledger under them. The tab opens on its forms, so without
 * the scroll the list the reader asked for sits a screen below them.
 */
export function openFamiliesInLab(): void {
  useStore.getState().setResearchOpen(true, 'evidence')
  let frames = 0
  const seat = () => {
    const list = document.querySelector<HTMLElement>('.research-family-list')
    if (!list && ++frames < LAB_FRAMES) {
      requestAnimationFrame(seat)
      return
    }
    const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    list?.scrollIntoView({ block: 'start', behavior: calm ? 'auto' : 'smooth' })
  }
  requestAnimationFrame(seat)
}
